/**
 * useImportDictionary — POST /enterprise/connectors/{conn_id}/import-dictionary.
 *
 * Two mutations over the same multipart endpoint, split by ``dry_run``:
 *
 *   - ``usePreviewDictionary`` (``dry_run=true``): reads the vendor Excel
 *     dictionary, matches it against the discovered catalogue and answers a
 *     coverage preview. Nothing is written, so no cache is touched.
 *   - ``useImportDictionary`` (``dry_run=false``): the BE answers 202 and runs
 *     the completion pipeline in the background — import the descriptions
 *     (marked ``user_edited`` so AI never overwrites them), sample code values
 *     without AI, then profile with the AI only the objects the dictionary
 *     does not cover. ``onSuccess`` invalidates schema, profiling and business
 *     context so the viewer and the status poll pick the new state up.
 *
 * Same style as ``useCollectValues``: the caller passes the ``connId`` in the
 * variables so the invalidation keys are derived from the mutation itself.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  type ImportDictionaryOptions,
  type ImportDictionaryPreview,
  type ImportDictionaryStarted,
  importEnterpriseDictionary,
} from "@ando/core/api";

import {
  enterpriseBusinessContextKey,
  enterpriseProfilingKey,
  enterpriseSchemaKey,
} from "./enterpriseKeys";

export interface UseImportDictionaryVars {
  readonly connId: string;
  readonly file: File;
  readonly opts?: ImportDictionaryOptions;
}

export function usePreviewDictionary() {
  return useMutation<ImportDictionaryPreview, Error, UseImportDictionaryVars>({
    mutationFn: ({ connId, file, opts }) =>
      importEnterpriseDictionary(connId, file, { ...opts, dryRun: true }),
  });
}

export function useImportDictionary() {
  const qc = useQueryClient();
  return useMutation<ImportDictionaryStarted, Error, UseImportDictionaryVars>({
    mutationFn: ({ connId, file, opts }) =>
      importEnterpriseDictionary(connId, file, { ...opts, dryRun: false }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: enterpriseSchemaKey(vars.connId) });
      void qc.invalidateQueries({ queryKey: enterpriseProfilingKey(vars.connId) });
      void qc.invalidateQueries({ queryKey: enterpriseBusinessContextKey(vars.connId) });
    },
  });
}
